import {Form, Spinner, OverlayTrigger, Tooltip} from 'react-bootstrap';
import React, {Dispatch, SetStateAction, useState} from 'react';
import {MeteringPoint} from '../../../models/electricity/metering-point';
import {ElectricityAgreementForm} from '../../../models/electricity/new-electricity-agreement';
import PDFPreviewModal from "components/Modals/PDFPreviewModal/PDFPreviewModal";
import ElectricityAgreementService from 'services/electricity/electricity-agreement.service';
import printJS from "print-js";

interface NewElectricityAgreementMeteringPointTableProps {
  id: string;
  meteringPoints: MeteringPoint[];
  setMeteringPoints: Dispatch<SetStateAction<MeteringPoint[]>>;
  form: ElectricityAgreementForm;

}

export function NewElectricityAgreementMeteringPointTable({
                                                     id,
                                                     meteringPoints,
                                                     setMeteringPoints,
                                                     form,
                                                   }: NewElectricityAgreementMeteringPointTableProps){

  if(!meteringPoints.length)
    return null;

  const allMeteringPointsChecked = !meteringPoints.some(mp => !mp.checked);

  return (
    <table className="new-electricity-agreement__metering-point-table">
      <thead>
      <tr>
        <th>
          <Form.Check
            id={id}
            custom
            type="checkbox"
            className='checkbox-large'
            label=''
            onChange={() =>
              setMeteringPoints( (mps) => {
                const remainingMeteringPoints = mps.filter(
                  mp => meteringPoints.every(meteringPoint => mp.id !== meteringPoint.id)
                );
                return [
                  ...remainingMeteringPoints,
                  ...meteringPoints.map(mp => ({...mp, checked: !allMeteringPointsChecked}))
                ]
              })
            }
            checked={allMeteringPointsChecked}
          />
        </th>
        <th>EIC</th>
        <th>AADRESS</th>
        {
          form.sellCheck && (
            <th className="d-flex flex-row align-items-center">
              TOOTMISVÕIMSUS(kW)
              <OverlayTrigger 
                delay={250} overlay={
                    <Tooltip id={`tooltip-${id}`}>
                        <p style={{fontSize: "0.75rem"}}>Valeandmete esitamise korral on Ostjal õigus leping lõpetada või kokkulepitud hinda muuta.</p>
                    </Tooltip>
                  }
                  placement={"bottom"}
                >
                  <i className="icon__info-black ml-1 d-none d-md-block"/>
              </OverlayTrigger>
            </th>
          )
        }
      </tr>
      </thead>
      <tbody>
      { meteringPoints.map(mp => (
        <TableRow
          key={mp.eic}
          meteringPoint={mp}
          setMeteringPoints={setMeteringPoints}
          form={form}
        />
      )) }
      </tbody>
    </table>
  )
}

interface TableRowProps {
  meteringPoint: MeteringPoint
  setMeteringPoints: Dispatch<SetStateAction<MeteringPoint[]>>;
  form : ElectricityAgreementForm;
}

const NUMERIC_REGEX = /^\d*[.,]?\d*$/;

function TableRow({
  meteringPoint,
  setMeteringPoints, 
  form
}: TableRowProps){
  const [pdfPreview, setPdfPreview] = useState<string | undefined>(undefined);
  const [isLoadingPdfPreview, setIsLoadingPdfPreview] = useState(false);
  const [isDownloadingAgreement, setIsDownloadingAgreement] = useState(false);

  const getRelatedAgreementPdfPreview = async () => {
    if(!relatedAgreement)
      return;

    try{
        setIsLoadingPdfPreview(true)

        const res = await ElectricityAgreementService.getElectricityAgreementPdf(relatedAgreement.id);
        setPdfPreview(res.data);
    }catch(e){
      console.error(e)
    }
    setIsLoadingPdfPreview(false);
  }

  const downloadAgreement = async () => {
    if(!relatedAgreement)
      return;

    try {
      setIsDownloadingAgreement(true);

      await ElectricityAgreementService.getElectricityAgreementPdfWithDownload(
        relatedAgreement.id, 
        `${relatedAgreement.name}.pdf`
      );

    } catch (e) {
        console.error(e);
    }
    setIsDownloadingAgreement(false);
  }

  const handleChangeProductionCapacity = (
    mp: MeteringPoint,
    e: React.ChangeEvent<any>
  ) => {
    if (!NUMERIC_REGEX.test(e.target.value)) { return; }
    const normalizedValue = e.target.value.replace(',', '.');

    setMeteringPoints(
      (meteringPoints) => meteringPoints.map(m => m.id === mp.id ? ({...m, productionCapacity: normalizedValue as any }) : m)
    )
  }
  
  const { 
    id, 
    eic, 
    terminationFee,
    checked,
    address, 
    productionCapacity, 
    relatedAgreement
  } = meteringPoint;

  return (
    <>
      <tr>
        <td>
          <Form.Check
            id={'check-' + eic}
            custom
            type="checkbox"
            className='checkbox-large'
            label=''
            onChange={() => setMeteringPoints((meteringPoints) => meteringPoints.map(m => m.id === id ? ({...m, checked: !m.checked }) : m))}
            checked={!!checked}
          />
        </td>
        <td>
          {eic}
          {terminationFee && (
            <div className='new-electricity-agreement__metering-point-table--termination-fee text-left mt-1'>
              <div>Kehtiva lepingu lõpetamine on tasuline!</div>
              <div>Lisainfo praeguselt elektrimüüjalt.</div>
            </div>
          )}
          {relatedAgreement && (
            <button  
              className="btn btn-link p-0 text-left d-flex align-items-center"
              onClick={() => getRelatedAgreementPdfPreview()}
            >
              Vaata seotud {relatedAgreement.startDate ? "lepingut" : "pakkumist"}
              { isLoadingPdfPreview && (
                <Spinner size={'sm'} animation={'border'} className="ml-1"/>
              )}
            </button>
          )}
        </td>
        <td>
          {address}
        </td>
        <td>
          {
            form.sellCheck && (
              <Form.Control
                value={productionCapacity}
                onChange={(e) => {
                  e.persist();
                  handleChangeProductionCapacity(meteringPoint, e)
                }}
                type="text"
              />
            )
          }
        </td>
      </tr>
      <PDFPreviewModal
        show={!!pdfPreview}
        onHide={() => setPdfPreview(undefined)}
        onDownload={() => downloadAgreement()}
        isDownloading={isDownloadingAgreement}
        onPrint={() => printJS({printable: pdfPreview!, type: 'pdf'})}
        pdfPreview={pdfPreview}
      />
    </>
  )
}